
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface NumberedPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  label: string;
  section: string;
}

const NumberedPagination: React.FC<NumberedPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  label,
  section
}) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 10;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      const startPage = Math.max(2, currentPage - 2);
      const endPage = Math.min(totalPages - 1, currentPage + 2);
      
      if (startPage > 2) {
        pages.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col items-center gap-4 mt-6">
      <div className="text-sm text-muted-foreground">
        {label} {currentPage} of {totalPages}
      </div>
      
      <nav className="flex items-center gap-1" aria-label={`${section} pagination`}>
        {currentPage > 1 && (
          <a
            href={`#${section}-page-${currentPage - 1}`}
            onClick={(e) => { 
              e.preventDefault(); 
              onPageChange(currentPage - 1);
              setTimeout(() => {
                document.getElementById(`${section}-section`)?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            className="flex items-center gap-1 px-3 py-2 text-sm text-primary hover:bg-muted rounded-md transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </a>
        )}
        
        {getPageNumbers().map((page, index) => (
          <span key={index}>
            {page === '...' ? (
              <span className="px-3 py-2 text-sm text-muted-foreground">...</span>
            ) : (
              <a
                href={`#${section}-page-${page}`}
                onClick={(e) => { 
                  e.preventDefault(); 
                  onPageChange(page as number);
                  setTimeout(() => {
                    document.getElementById(`${section}-section`)?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className={`px-3 py-2 text-sm rounded-md transition-colors ${
                  currentPage === page
                    ? 'bg-primary text-primary-foreground'
                    : 'text-primary hover:bg-muted'
                }`}
                aria-label={`Page ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </a>
            )}
          </span>
        ))}
        
        {currentPage < totalPages && (
          <a
            href={`#${section}-page-${currentPage + 1}`}
            onClick={(e) => { 
              e.preventDefault(); 
              onPageChange(currentPage + 1);
              setTimeout(() => {
                document.getElementById(`${section}-section`)?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            className="flex items-center gap-1 px-3 py-2 text-sm text-primary hover:bg-muted rounded-md transition-colors"
            aria-label="Next page"
          >
            Siguiente
            <ChevronRight className="h-4 w-4" />
          </a>
        )}
      </nav>
    </div>
  );
};

export default NumberedPagination;
